'use client';

import React from 'react';
import {
  FragmentMediaImageFragment,
  FragmentParagraphMediaFragment,
} from '@/utils/drupal-api/__generated__/graphql';
import { ImageSrcSetSize } from '@/utils/image';
import clsx from 'clsx';

import dynamic from 'next/dynamic';

const CmsCarousel = dynamic(() => import('@/components/ui/cms-carousel'));
import CmsGallery from '@/components/ui/cms-gallery';

import MediaUnionComponent from '../media_union';
import styles from './media.module.scss';
import paragraphStyles from './paragraph.module.scss';

export type MediaProps = {
  data: FragmentParagraphMediaFragment;
  className?: string;
  imageSizes?: ImageSrcSetSize[];
  imageAspect?: number;
  id?: string;
};

export const Media: React.FC<MediaProps> = ({
  data,
  className,
  imageSizes,
  imageAspect,
  id,
}) => {
  if (data.mediaAspect && !imageAspect) {
    const [width, height] = data.mediaAspect.split('_').map((x) => parseInt(x));
    imageAspect = width / height;
  }

  const getImageSizes = () => {
    if (imageSizes) {
      return imageSizes;
    }
    switch (data.displayFormat) {
      case 'carousel':
        return [
          { viewport: 320, image: 320 },
          { viewport: 600, image: 600 },
        ];
      case 'gallery':
        return [
          { viewport: 320, image: 320 },
          { viewport: 600, image: 600 },
        ];
      case 'inline':
        return [
          { image: 380, viewport: 375 },
          { image: 420, viewport: 420 },
          { image: 600, viewport: 600 },
          { image: 800, viewport: 800 },
          { image: 1024, viewport: 1024 },
        ];
      default:
        return [];
    }
  };
  const renderMediaContent = () => {
    switch (data.displayFormat) {
      case 'carousel': {
        const mediaSlides = data.media.map((mediaItem, index) => {
          const isImage = mediaItem.__typename === 'MediaImage';
          return {
            id: index,
            content: (
              <MediaUnionComponent
                data={mediaItem}
                imageAspect={imageAspect}
                imageSizes={getImageSizes()}
                imageClassName="aspect-square object-cover w-full h-full"
              />
            ),
            ...(isImage && {
              original: mediaItem.mediaImage.url,
              width: mediaItem.mediaImage.width,
              height: mediaItem.mediaImage.height,
              alt: mediaItem.mediaImage.alt,
              caption: mediaItem.caption,
            }),
          };
        });
        return (
          <CmsCarousel
            slides={mediaSlides}
            options={{ loop: true, align: 'center' }}
            enableGallery={true}
            slideContainerClassName="flex"
            slideWrapperClassName="flex-[0_0_80%] min-w-0 px-2.5 lg:px-3 py-3 sm:flex-[0_0_50%] lg:flex-[0_0_33.333%]"
          />
        );
      }
      case 'gallery':
        return (
          <div className="cms-gallery">
            <CmsGallery
              data={data.media.filter(
                (item): item is FragmentMediaImageFragment =>
                  item.__typename === 'MediaImage'
              )}
              galleryClassName="flex flex-wrap items-center justify-center"
              itemClassName="flex-[0_0_50%] lg:flex-[0_0_33.333%] p-2"
              slideClassName="object-cover w-full h-full"
              sectionClassName="aspect-square overflow-hidden"
              figureClassName="w-full h-full"
              imageAspectThumb={1}
              imageSizesThumb={getImageSizes()}
            />
          </div>
        );
      case 'inline':
        return (
          <>
            {data.media.map((mediaItem, index) => (
              <MediaUnionComponent
                key={index}
                data={mediaItem}
                imageAspect={imageAspect}
                imageSizes={getImageSizes()}
                figureClassName="mx-auto"
                imageClassName="mx-auto"
                className="mb-4 mt-2 flex flex-col items-center justify-center"
              />
            ))}
          </>
        );
      default:
        return null;
    }
  };

  return (
    <section
      className={clsx(
        paragraphStyles.paragraph,
        styles.paragraphMedia,
        className,
        'paragraph',
        'paragraph-media'
      )}
      id={id}
    >
      {data.header && (
        <h3
          className={clsx(
            'h4 text-center text-gray-8',
            data.displayFormat === 'gallery' && 'mb-2'
          )}
        >
          {data.header}
        </h3>
      )}
      {renderMediaContent()}
    </section>
  );
};
